import React from 'react';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { AuthProvider } from './context/AuthContext';
import AuthGuard from './components/AuthGuard';
import AuthCallback from './components/AuthCallback';
import MainLayout from './components/MainLayout';
import './App.css';

class ErrorBoundary extends React.Component<{ children: React.ReactNode }, { hasError: boolean; error: any }> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error: any) {
    return { hasError: true, error };
  }
  componentDidCatch(error: any, errorInfo: any) {
    console.error('ErrorBoundary caught:', error, errorInfo);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{ color: 'red', padding: 32 }}>
          <h2>Something went wrong.</h2>
          <pre>{String(this.state.error)}</pre>
        </div>
      );
    }
    return this.props.children;
  }
}

const theme = createTheme({
  palette: {
    primary: {
      main: '#308E75',
    },
    secondary: {
      main: '#62D3B0',
    },
    background: {
      default: '#F5F5F5',
    },
    text: {
      primary: '#1D1E1E',
      secondary: '#666666',
    },
    error: {
      main: '#E1502F',
    },
    warning: {
      main: '#FFCF08',
    },
    success: {
      main: '#80A405',
    },
  },
  typography: {
    fontFamily: 'Montserrat, "Open Sans", "Helvetica Neue", Helvetica, Arial, sans-serif',
    h1: { fontSize: '32px', fontWeight: 700, lineHeight: 1.2 },
    h2: { fontSize: '28px', fontWeight: 700, lineHeight: 1.2 },
    h3: { fontSize: '24px', fontWeight: 700, lineHeight: 1.2 },
    h4: { fontSize: '20px', fontWeight: 700, lineHeight: 1.2 },
    h5: { fontSize: '18px', fontWeight: 700, lineHeight: 1.2 },
    h6: { fontSize: '16px', fontWeight: 700, lineHeight: 1.2 },
    body1: { fontSize: '14px', fontWeight: 400, lineHeight: 1.5 },
    body2: { fontSize: '13px', fontWeight: 400, lineHeight: 1.5 },
    subtitle1: { fontSize: '14px', fontWeight: 500 },
    subtitle2: { fontSize: '12px', fontWeight: 500 },
  },
  shape: {
    borderRadius: 8,
  },
  spacing: 8,
});

function App() {
  // Check if this is the auth callback route
  if (window.location.pathname === '/auth/callback') {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <AuthProvider>
          <AuthCallback />
        </AuthProvider>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <AuthGuard>
          <ErrorBoundary>
            <MainLayout />
          </ErrorBoundary>
        </AuthGuard>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;

